import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Col,
  Form,
  Input,
  Layout,
  Row,
  Spin,
  Typography,
  Table,
  Descriptions,
  notification,
} from 'antd';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import {
  createProfile,
  getProfile,
  getModulesProfile,
  updateProfileModules,
} from '../../../api/module/profile';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import { CustomButton } from '../../../components';
import { catchException } from '../../../utils/helpers/helpers';
import MESSAGES from '../../../enums/Messages';
import { useLocation } from 'react-router-dom';

const { Content } = Layout;
const { Title } = Typography;
const { TextArea } = Input;

export default function ProfileForm({ match, history, id }) {
  const [formCreate] = Form.useForm();
  const [formEdit] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [isEdit, setIsEdit] = useState(id ? true : false);
  const [profile, setProfile] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const isDuplicating = queryParams.get('isDuplicating') === 'true';
  const [oldProfile, setOldProfile] = useState(
    queryParams.get('oldProfile') || 0,
  );

  const breadcrumbItems = [
    {
      text: 'Perfiles',
      to: `${INTERNAL_LINKS.PROJECTS_PROFILE}`,
    },
    {
      text:
        isEdit && !isDuplicating
          ? 'Editar perfil'
          : isDuplicating
          ? 'Duplicar perfil'
          : 'Crear perfil',
    },
  ];

  const columns = [
    {
      title: 'ID',
      dataIndex: 'profile_module_type_id',
      key: 'profile_module_type_id',
      align: 'center',
    },
    {
      title: 'Módulo',
      dataIndex: 'profile_module_type_nombre',
      key: 'profile_module_type_nombre',
    },
    {
      title: 'Insertar',
      dataIndex: 'insertar',
      key: 'insertar',
      align: 'center',
      render: (text, record) => (
        <Checkbox
          checked={record.insertar}
          onChange={() => handleCheckboxChange(record, 'insertar')}
        />
      ),
    },
    {
      title: 'Leer',
      dataIndex: 'leer',
      key: 'leer',
      align: 'center',
      render: (text, record) => (
        <Checkbox
          checked={record.leer}
          onChange={() => handleCheckboxChange(record, 'leer')}
        />
      ),
    },
    {
      title: 'Editar',
      dataIndex: 'editar',
      key: 'actualizar',
      align: 'center',
      render: (text, record) => (
        <Checkbox
          checked={record.actualizar}
          onChange={() => handleCheckboxChange(record, 'actualizar')}
        />
      ),
    },
    {
      title: 'Borrar',
      dataIndex: 'eliminar',
      key: 'eliminar',
      align: 'center',
      render: (text, record) => (
        <Checkbox
          checked={record.eliminar}
          onChange={() => handleCheckboxChange(record, 'eliminar')}
        />
      ),
    },
  ];

  const handleCheckboxChange = (record, field) => {
    const newDataSource = dataSource.map((item) => {
      if (item.profile_module_type_id === record.profile_module_type_id) {
        return { ...item, [field]: !item[field] };
      }
      return item;
    });
    setDataSource(newDataSource);
  };

  useEffect(() => {
    async function fetchProfile() {
      try {
        let res = await getProfile(id);
        setProfile(res.data);
      } catch (error) {
        catchException(error);
      }
    }
    async function fetchModuleProfile() {
      try {
        let res = await getModulesProfile(oldProfile !== 0 ? oldProfile : id);
        setDataSource(res.data.results);
      } catch (error) {
        catchException(error);
      }
    }

    (isEdit || isDuplicating) && fetchProfile();
    isEdit && !isDuplicating && fetchModuleProfile();
  }, [isEdit, isDuplicating]);

  useEffect(() => {
    if (profile) {
      if (isEdit && !isDuplicating) {
        formEdit.setFieldsValue({
          nombre: profile.nombre,
          descripcion: profile.descripcion,
        });
      } else if (isDuplicating) {
        formCreate.setFieldsValue({
          nombre: profile.nombre,
          descripcion: profile.descripcion,
        });
      }
    }
  }, [profile]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      if (isEdit && !isDuplicating) {
        const updateData = {
          perfil: id, // ID del perfil
          profile_modules: dataSource.map((item) => {
            return {
              tipo: item.profile_module_type_id,
              insertar: item.insertar ? item.insertar : false,
              leer: item.leer ? item.leer : false,
              actualizar: item.actualizar ? item.actualizar : false,
              eliminar: item.eliminar ? item.eliminar : false,
            };
          }),
        };
        await updateProfileModules(updateData);
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
        setTimeout(() => {
          history.push(`${INTERNAL_LINKS.PROJECTS_PROFILE}`);
        }, 2000);
      } else {
        let res = await createProfile(values);
        notification.success({
          message: !isDuplicating
            ? MESSAGES.DESC_PROFILE_SUCCESS_MSG
            : MESSAGES.DESC_PROFILE_DUPLICATE_SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
        setTimeout(() => {
          if (!isDuplicating)
            history.push(
              `${INTERNAL_LINKS.PROJECTS_PROFILE}-new/${res.data.id}`,
            );
          else
            history.push(
              `${INTERNAL_LINKS.PROJECTS_PROFILE}-new/${res.data.id}?oldProfile=${id}`,
            );
        }, 2000);
      }
    } catch (error) {
      catchException(error);
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <Content>
      <Row>
        <Col span={24}>
          <BreadcrumbContainer items={breadcrumbItems} />
        </Col>
        <Col span={24}>
          <Title className={'text-uppercase'} level={2}>
            Gestión de proyectos - perfiles
          </Title>
        </Col>
      </Row>
      {!isEdit || (isDuplicating && profile) ? (
        <Form
          name="create-profile"
          form={formCreate}
          labelCol={{ xs: { span: 24 }, sm: { span: 6 }, md: { span: 4 } }}
          wrapperCol={{ xs: { span: 24 }, sm: { span: 18 }, md: { span: 16 } }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          layout="vertical"
        >
          <Form.Item
            label="Nombre del perfil"
            name="nombre"
            rules={[{ required: true, message: 'Nombre del perfil requerido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Descripción"
            name="descripcion"
            rules={[
              { required: true, message: 'Descripción del perfil requerido' },
            ]}
          >
            <TextArea rows={2} autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
          <Form.Item wrapperCol={{ span: 24 }}>
            <CustomButton
              type="primary"
              htmlType="submit"
              text="Crear perfil"
              loading={loading}
            />
            <CustomButton
              style={{ marginLeft: '20px' }}
              type="error"
              danger
              htmlType="button"
              text="Cerrar"
              onClick={() => history.push(`${INTERNAL_LINKS.PROJECTS_PROFILE}`)}
            />
          </Form.Item>
        </Form>
      ) : (
        isEdit &&
        isDuplicating &&
        !profile && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Spin size="large" />
          </div>
        )
      )}
      {isEdit && !isDuplicating && profile ? (
        <>
          <Form
            form={formEdit}
            name="edit-profile"
            labelCol={{ xs: { span: 24 }, sm: { span: 6 }, md: { span: 4 } }}
            wrapperCol={{
              xs: { span: 24 },
              sm: { span: 18 },
              md: { span: 16 },
            }}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            autoComplete="off"
            layout="vertical"
          >
            <Descriptions
              layout="horizontal"
              column={2}
              bordered
              labelStyle={{ maxWidth: '8rem' }}
            >
              <Descriptions.Item label="Nombre del perfil" span={2}>
                {profile?.nombre}
              </Descriptions.Item>
              <Descriptions.Item label="Descripción">
                {profile?.descripcion}
              </Descriptions.Item>
            </Descriptions>
            <Table
              columns={columns}
              dataSource={dataSource}
              pagination={false}
            />
            <Form.Item wrapperCol={{ span: 24 }}>
              <CustomButton
                type="primary"
                htmlType="submit"
                text="Guardar cambios"
                loading={loading}
              />
              <CustomButton
                style={{ marginLeft: '20px' }}
                type="error"
                danger
                htmlType="button"
                text="Cerrar"
                onClick={() =>
                  history.push(`${INTERNAL_LINKS.PROJECTS_PROFILE}`)
                }
              />
            </Form.Item>
          </Form>
        </>
      ) : (
        isEdit &&
        !profile &&
        !isDuplicating && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Spin size="large" />
          </div>
        )
      )}
    </Content>
  );
}
